import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MANEDER = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

function beregnForecast(params, nuvaerende_mrr) {
  const { nyeKlienter, arpu, churnRate, b2bAftaler, fbKonvertering } = params;
  const ekstraFraFB = (nyeKlienter * fbKonvertering) / 100;
  const totalNye = nyeKlienter + ekstraFraFB;

  const data = [];
  let mrr = nuvaerende_mrr;
  for (let i = 0; i < 12; i++) {
    mrr = mrr * (1 - churnRate / 100) + totalNye * arpu + b2bAftaler * 3000;
    data.push({
      md: MANEDER[i],
      baseline: Math.round(mrr),
      optimistisk: Math.round(mrr * 1.2),
      pessimistisk: Math.round(mrr * 0.8),
    });
  }
  return data;
}

function getScenarieName(finalMrr) {
  if (finalMrr >= 420000) return { navn: 'Ambitioest', color: '#3b82f6' };
  if (finalMrr >= 290000) return { navn: 'Realistisk', color: '#22c55e' };
  if (finalMrr >= 120000) return { navn: 'Konservativt', color: '#f59e0b' };
  return { navn: 'Under maal', color: '#ef4444' };
}

function Slider({ label, min, max, step, value, onChange, suffix }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ color: '#d1d5db', fontSize: 13 }}>{label}</span>
        <span style={{ color: '#ffffff', fontWeight: 600, fontSize: 13 }}>{value}{suffix}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: '#2322be' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280', fontSize: 11 }}>
        <span>{min}{suffix}</span><span>{max}{suffix}</span>
      </div>
    </div>
  );
}

export default function ScenariesimulatoriVPanel() {
  const [nyeKlienter, setNyeKlienter] = useState(15);
  const [arpu, setArpu] = useState(1385);
  const [churnRate, setChurnRate] = useState(5);
  const [b2bAftaler, setB2bAftaler] = useState(0);
  const [fbKonvertering, setFbKonvertering] = useState(2);

  const nuvaerende_mrr = 0;

  const forecast = useMemo(() =>
    beregnForecast({ nyeKlienter, arpu, churnRate, b2bAftaler, fbKonvertering }, nuvaerende_mrr),
    [nyeKlienter, arpu, churnRate, b2bAftaler, fbKonvertering]
  );

  const finalMrr = forecast[forecast.length - 1]?.baseline || 0;
  const scenarie = getScenarieName(finalMrr);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        {/* Slidere */}
        <div style={{ flex: 1, minWidth: 250 }}>
          <Slider label="Nye klienter/md" min={5} max={50} step={1} value={nyeKlienter} onChange={setNyeKlienter} suffix="" />
          <Slider label="ARPU (kr)" min={500} max={5000} step={50} value={arpu} onChange={setArpu} suffix=" kr" />
          <Slider label="Churn-rate" min={0} max={20} step={0.5} value={churnRate} onChange={setChurnRate} suffix="%" />
          <Slider label="B2B-aftaler/md" min={0} max={10} step={1} value={b2bAftaler} onChange={setB2bAftaler} suffix="" />
          <Slider label="Facebook konvertering" min={0} max={10} step={0.5} value={fbKonvertering} onChange={setFbKonvertering} suffix="%" />
        </div>
        {/* Graf */}
        <div style={{ flex: 2, minWidth: 250 }}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={forecast} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="md" tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip
                contentStyle={{ background: '#1e2235', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }}
                formatter={v => [`${v.toLocaleString('da-DK')} kr`]}
              />
              <Legend wrapperStyle={{ color: '#9ca3af', fontSize: 12 }} />
              <Line type="monotone" dataKey="optimistisk" name="Optimistisk (+20%)" stroke="#22c55e" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
              <Line type="monotone" dataKey="baseline" name="Baseline" stroke="#3b82f6" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="pessimistisk" name="Pessimistisk (-20%)" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: 12, padding: '10px 16px', background: `${scenarie.color}22`, borderRadius: 8, border: `1px solid ${scenarie.color}55` }}>
            <span style={{ color: scenarie.color, fontWeight: 700, fontSize: 15 }}>
              Om 12 maneder: {finalMrr.toLocaleString('da-DK')} kr MRR
            </span>
            <span style={{ color: '#9ca3af', fontSize: 13, marginLeft: 8 }}>→ {scenarie.navn}</span>
          </div>
        </div>
      </div>
    </div>
  );
}